// ============================================================================
// PROJECT OVERVIEW TAB
// Dashboard view showing stats, recent sessions, activity feed, quick actions
// Redesigned from Stitch mockup: "Spectrum Project Overview View"
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Users, FileText, CheckCircle, Plus, Upload, UserPlus,
  ArrowRight, FileSpreadsheet
} from 'lucide-react';
import { Project } from '../../types';
import { useProjectSessions } from '../../hooks/useProjects';
import { projectTasksService } from '../../services/sessionTasksService';

interface OverviewTabProps {
  project: Project;
  onViewChange?: (view: string) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ project, onViewChange }) => {
  const navigate = useNavigate();
  const { data: sessions } = useProjectSessions(project.projectId);
  const [taskCount, setTaskCount] = useState<number>(0);

  useEffect(() => {
    projectTasksService.listTasks(project.projectId)
      .then(tasks => setTaskCount(tasks.length))
      .catch(() => {});
  }, [project.projectId]);

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatSessionDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPhaseLabel = (session: any): string => {
    return session.currentPhase || session.phase || 'Discovery';
  };

  const allSessions = [...(sessions || [])].sort((a: any, b: any) => {
    const aDate = new Date(a.createdAt || a.startedAt || 0).getTime();
    const bDate = new Date(b.createdAt || b.startedAt || 0).getTime();
    return bDate - aDate;
  });
  const teamMembers = project.acl || [];

  return (
    <div className="flex flex-col gap-8">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Sessions</p>
            <p className="text-2xl font-bold text-slate-900">{project.sessionCount || 0}</p>
          </div>
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <Calendar size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Members</p>
            <p className="text-2xl font-bold text-slate-900">{(teamMembers.length || 0) + 1}</p>
          </div>
          <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
            <Users size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Documents</p>
            <p className="text-2xl font-bold text-slate-900">{project.documentCount || 0}</p>
          </div>
          <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
            <FileText size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Tasks</p>
            <p className="text-2xl font-bold text-slate-900">{taskCount}</p>
          </div>
          <div className="p-2 bg-green-100 rounded-lg text-green-600">
            <CheckCircle size={24} />
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 flex flex-col gap-8">
          {/* Sessions */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h3 className="font-bold text-[#052831] text-lg">Sessions</h3>
              <span className="text-xs font-medium text-[#64748b]">{allSessions.length} total</span>
            </div>
            <div className="divide-y divide-slate-50 overflow-y-auto max-h-[400px]">
              {allSessions.length > 0 ? (
                allSessions.map((session: any) => (
                  <div
                    key={session.sessionId || session.id}
                    onClick={() => navigate(`/session/${session.sessionId || session.id}`)}
                    className="px-6 py-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors cursor-pointer"
                  >
                    <div className="flex flex-col gap-1">
                      <h4 className="font-semibold text-[#052831]">
                        {session.customerName || session.name || session.sessionId || session.id}
                      </h4>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#64748b]">
                          {formatSessionDate(session.createdAt || session.startedAt || new Date().toISOString())}
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-bold uppercase">
                          {getPhaseLabel(session)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        session.status === 'active' ? 'bg-green-500' :
                        session.status === 'ended' ? 'bg-slate-400' : 'bg-amber-400'
                      }`} />
                      <ArrowRight size={16} className="text-slate-300" />
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-10 text-center text-[#64748b] text-sm">
                  No sessions yet. Create your first session to get started.
                </div>
              )}
            </div>
          </section>

          {/* Activity Feed */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden p-6">
            <h3 className="font-bold text-[#052831] text-lg mb-6">Activity Feed</h3>
            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-[7px] before:w-0.5 before:bg-slate-100">
              {project.lastActivityAt ? (
                <ActivityEntry
                  text="Project last updated"
                  timestamp={formatRelativeTime(project.lastActivityAt)}
                  detail={`Status: ${project.status || 'active'}`}
                />
              ) : null}
              <ActivityEntry
                text="Project created"
                timestamp={formatRelativeTime(project.createdAt)}
                detail={`by ${project.ownerEmail}`}
              />
              {allSessions.length > 0 && (
                <ActivityEntry
                  text={`${allSessions.length} session${allSessions.length > 1 ? 's' : ''} linked to this project`}
                  timestamp=""
                  detail="View sessions in the sidebar"
                />
              )}
            </div>
          </section>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-8">
          {/* Quick Actions */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-6">
            <h3 className="font-bold text-[#052831] text-lg mb-4">Quick Actions</h3>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate('/create')}
                className="w-full py-2.5 bg-primary text-white font-bold rounded-xl hover:brightness-105 transition-all flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                New Session
              </button>
              <button
                onClick={() => onViewChange?.('knowledge-base')}
                className="w-full py-2.5 bg-primary/10 text-primary font-bold rounded-xl hover:bg-primary/20 transition-all flex items-center justify-center gap-2"
              >
                <Upload size={18} />
                Upload Document
              </button>
              <button
                onClick={() => onViewChange?.('access')}
                className="w-full py-2.5 bg-white border border-[#e2e8f0] text-[#64748b] font-bold rounded-xl hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
              >
                <UserPlus size={18} />
                Invite Members
              </button>
            </div>
          </section>

          {/* Knowledge Base Preview */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#052831] text-lg">Knowledge Base</h3>
            </div>
            {project.documentCount > 0 ? (
              <div className="space-y-3">
                <KBPlaceholderItem icon="pdf" name="Documents available" detail={`${project.documentCount} files`} />
              </div>
            ) : (
              <p className="text-sm text-[#64748b] text-center py-4">No documents uploaded yet</p>
            )}
          </section>

          {/* Team */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-6">
            <h3 className="font-bold text-[#052831] text-lg mb-4">Project Team</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Owner */}
              <TeamAvatar email={project.ownerEmail} highlight />
              {/* ACL members */}
              {teamMembers.slice(0, 4).map((member: any) => (
                <TeamAvatar key={member.userId} email={member.email} />
              ))}
              {teamMembers.length > 4 && (
                <div className="w-10 h-10 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-xs font-bold text-[#64748b]">
                  +{teamMembers.length - 4}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ACTIVITY ENTRY
// ============================================================================

interface ActivityEntryProps {
  text: string;
  timestamp: string;
  detail: string;
}

const ActivityEntry: React.FC<ActivityEntryProps> = ({ text, timestamp, detail }) => (
  <div className="relative pl-8">
    <span className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full bg-primary border-4 border-white shadow-sm" />
    <div>
      <p className="text-sm text-[#052831] font-medium">
        {text} {timestamp && <span className="text-[#94a3b8] font-normal">· {timestamp}</span>}
      </p>
      <p className="text-xs text-[#64748b] mt-1">{detail}</p>
    </div>
  </div>
);

// ============================================================================
// KB PLACEHOLDER ITEM
// ============================================================================

interface KBPlaceholderItemProps {
  icon: 'pdf' | 'doc' | 'sheet';
  name: string;
  detail: string;
}

const KBPlaceholderItem: React.FC<KBPlaceholderItemProps> = ({ icon, name, detail }) => {
  const iconColors: Record<string, { bg: string; text: string; Icon: React.ElementType }> = {
    pdf: { bg: 'bg-red-50', text: 'text-red-500', Icon: FileText },
    doc: { bg: 'bg-blue-50', text: 'text-blue-500', Icon: FileText },
    sheet: { bg: 'bg-emerald-50', text: 'text-emerald-500', Icon: FileSpreadsheet },
  };
  const { bg, text, Icon } = iconColors[icon] || iconColors.pdf;

  return (
    <div className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors group">
      <div className={`w-10 h-10 rounded-lg ${bg} ${text} flex items-center justify-center`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#052831] truncate">{name}</p>
        <p className="text-[10px] text-[#94a3b8] uppercase font-bold tracking-tight">{detail}</p>
      </div>
    </div>
  );
};

// ============================================================================
// TEAM AVATAR
// ============================================================================

interface TeamAvatarProps {
  email: string;
  highlight?: boolean;
}

const TeamAvatar: React.FC<TeamAvatarProps> = ({ email, highlight }) => {
  const initials = email
    .split('@')[0]
    .split(/[._-]/)
    .map(p => p[0]?.toUpperCase() || '')
    .join('')
    .slice(0, 2);

  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
        highlight
          ? 'bg-primary/20 text-primary border-primary/30'
          : 'bg-slate-100 text-[#64748b] border-slate-200'
      }`}
      title={email}
    >
      {initials}
    </div>
  );
};
